import type { WalletContextState } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import type { PreparedReleaseTransaction } from '@src/dto';
import { canonicalizeArweaveGatewayUrl } from '../shared/arweave/publicArweaveGateway';
import { toast } from 'sonner';
import type { TRPC } from '../contexts/TRPC';
import { isLeftResult } from '../lib/trpcTypeGuards';
import { isBlockhashStaleError, MAX_BLOCKHASH_RETRIES } from './blockhash';
import { getErrorMessage, isUserRejectedError } from './errorHandling';
import { captureAndReportError } from './errorReporting';
import { deduplicateTransactionSigners } from './transactionHelpers';

type TRPCClient = TRPC['client'];

interface MintReleaseNftParams {
  trpc: TRPCClient;
  wallet: WalletContextState;
  releaseId: string;
  releaseName: string;
  releaseMetadataUri: string;
  appMintAddress: string;
  publisherAddress: string;
}

interface MintReleaseNftResult {
  preparedTx: PreparedReleaseTransaction;
  transactionSignature: string;
}

export async function mintReleaseNftWithRetry({
  trpc,
  wallet,
  releaseId,
  releaseName,
  releaseMetadataUri,
  appMintAddress,
  publisherAddress,
}: MintReleaseNftParams): Promise<MintReleaseNftResult> {
  if (!wallet.publicKey) {
    throw new Error('Wallet public key is unavailable');
  }

  if (!wallet.signTransaction) {
    throw new Error('Wallet does not support transaction signing');
  }

  const metadataUri = canonicalizeArweaveGatewayUrl(releaseMetadataUri);

  for (let attempt = 1; attempt <= MAX_BLOCKHASH_RETRIES; attempt++) {
    try {
      const preparedTxResult =
        await trpc.release.prepareReleaseNftTransaction.mutate({
          releaseId,
          releaseName,
          releaseMetadataUri: metadataUri,
          appMintAddress,
          publisherAddress,
          payerAddress: wallet.publicKey.toBase58(),
        });

      if (isLeftResult(preparedTxResult)) {
        throw new Error(preparedTxResult.left.message);
      }

      const preparedTx = preparedTxResult.right;

      const transaction = Transaction.from(
        Buffer.from(preparedTx.transaction, 'base64'),
      );

      deduplicateTransactionSigners(transaction, preparedTx.mintAddress);

      let signedTransaction: Transaction;
      try {
        signedTransaction = await wallet.signTransaction(transaction);
      } catch (signError) {
        const errorMessage = getErrorMessage(signError);

        if (isUserRejectedError(errorMessage)) {
          throw new Error('User rejected the request');
        }
        throw signError;
      }

      const submitResult = await trpc.submitNftTransaction.mutate({
        signedTransaction: signedTransaction.serialize().toString('base64'),
      });

      if (isLeftResult(submitResult)) {
        throw new Error(submitResult.left.message);
      }

      return {
        preparedTx,
        transactionSignature: submitResult.right.transactionSignature,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      if (
        isBlockhashStaleError(errorMessage) &&
        attempt < MAX_BLOCKHASH_RETRIES
      ) {
        toast.warning(
          'The mint transaction expired before reaching the network. Refreshing the blockhash—please approve the transaction again.',
          { duration: 8000 },
        );
        console.warn(
          'Release mint attempt hit a stale blockhash, retrying...',
          {
            attempt,
            error: errorMessage,
          },
        );
        continue;
      }

      if (!isUserRejectedError(errorMessage)) {
        captureAndReportError(error, {
          operation: 'Release NFT mint with retry failed',
          component: 'mintReleaseNftWithRetry',
          nft_type: 'release',
          stage: 'frontend_mint_retry',
          releaseId: releaseId,
          releaseName: releaseName,
          releaseMetadataUri: metadataUri,
          appMintAddress: appMintAddress,
          publisherAddress: publisherAddress,
          walletAddress: wallet.publicKey?.toBase58(),
          attempt: attempt,
          maxAttempts: MAX_BLOCKHASH_RETRIES,
          isBlockhashError: isBlockhashStaleError(errorMessage),
          errorMessage: errorMessage,
        });
      }

      throw error;
    }
  }

  throw new Error('Failed to mint Release NFT. Please try again.');
}
